import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ActionEntityType } from '@pmp/api';
import { usePageContextStore } from '@pmp/stores';
import { isListOnScreen, LIST_URL_SPECS, type ListColumnFilters } from '@pmp/utils';
import type { FiltersReader, RowCountsReader } from '../assistant/pageContext';

import { useFeatureToggle } from './useFeatureToggle';

/** Table props that report the list to the assistant. Empty when the assistant is off. */
export interface AssistantTableProps<T> {
  onChangeSelectedRows?: (selectedRows: T[]) => void;
  onGlobalSearch?: (searchText: string) => void;
  onFiltersChange?: (filters: ListColumnFilters) => void;
}

// What the assistant calls this list. ActionEntityType covers every list a change request can target;
// the others have no member there because nothing about them is change-requestable.
export type AssistantListType = ActionEntityType | 'CHANGE_REQUESTS' | 'REGIONS' | 'BDMS';

// Feeds one list's state into the assistant's page context. Spread the result onto the
// enso Table and Ohm can resolve "these two", "the selected ones" and "this one" to real records:
//
//   const assistantContext = useAssistantPageContext<FeeDefinition>(entityType, rowKey);
//   const tableProps = useMemo(() => ({ ...assistantContext, id: entityType, ... }), [assistantContext, ...]);
//
// Only ids and metadata leave the browser, never cell values: the agent re-reads every value through
// its own tools with the caller's token (see assistant/pageContext.ts).
//
// Gated on `enableAssistant`: with the flag off it returns no props at all, so the table calls
// nothing, the store is never written and a tenant without Ohm pays nothing for it.
// A list the agent has no tool for passes `undefined` and reports nothing: a list the agent is told about
// but cannot read invites it to answer from whichever collection it can read.
export const useAssistantPageContext = <T,>(
  entityType: AssistantListType | undefined,
  rowKey: string,
  readRowCounts?: RowCountsReader,
  readFilters?: FiltersReader,
): AssistantTableProps<T> => {
  const [searchParams] = useSearchParams();
  const spec = entityType === undefined ? undefined : LIST_URL_SPECS[entityType];

  // A route rendering more than one list keeps every mounted list reporting, and the store then resolves
  // the page from whichever reported last, which is not the one on screen. So a registered list on a
  // tabbed route reports only while its tab is open, and the hidden one clears itself.
  const onScreen = spec === undefined || isListOnScreen(searchParams, spec);
  const enabled = useFeatureToggle(['enableAssistant']) && entityType !== undefined && onScreen;
  const { pathname } = useLocation();
  const reportList = usePageContextStore(state => state.reportList);
  const clearList = usePageContextStore(state => state.clearList);

  // Reported on mount so the assistant knows which list the user is looking at before any row is
  // selected, and cleared on unmount so a list the user has left never describes the current page.
  // `enabled` is a dependency, so turning the flag off mid-session also clears what was reported.
  useEffect(() => {
    if (!enabled || entityType === undefined) return;

    reportList(entityType, { route: pathname, readRowCounts, readFilters });
    return () => clearList(entityType);
  }, [clearList, enabled, entityType, pathname, readFilters, readRowCounts, reportList]);

  const onChangeSelectedRows = useCallback(
    (selectedRows: T[]) => {
      if (entityType === undefined) return;
      const selectedIds = selectedRows.map(row => (row as Record<string, unknown>)[rowKey]);
      reportList(entityType, { route: pathname, selectedIds, readRowCounts });
    },
    [entityType, pathname, readRowCounts, reportList, rowKey],
  );

  // Fires on confirm (Enter or the search button), which is the default globalSearchMode.
  const onGlobalSearch = useCallback(
    (searchText: string) => {
      if (entityType === undefined) return;
      reportList(entityType, { route: pathname, searchActive: searchText.trim() !== '', readRowCounts });
    },
    [entityType, pathname, readRowCounts, reportList],
  );

  // What the grid is filtered by, so the agent can offer a link to the set on screen rather than to the
  // whole list, and can tell that a count it derived was already narrowed by the user.
  const onFiltersChange = useCallback(
    (filters: ListColumnFilters) => {
      if (entityType === undefined) return;
      reportList(entityType, { route: pathname, activeFilters: filters, readRowCounts });
    },
    [entityType, pathname, readRowCounts, reportList],
  );

  return useMemo(
    () => (enabled ? { onChangeSelectedRows, onGlobalSearch, onFiltersChange } : {}),
    [enabled, onChangeSelectedRows, onFiltersChange, onGlobalSearch],
  );
};
